import { useState, useRef, useCallback } from "react";
import { HiOutlineEnvelope } from "react-icons/hi2";

export default function PostcardGenerator({ stampImageUrl, stampSubject }) {
  const [to, setTo] = useState("");
  const [from, setFrom] = useState("");
  const [message, setMessage] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const canvasRef = useRef(null);

  const wrapText = useCallback(
    (ctx, text, x, y, maxWidth, lineHeight, { justify = false } = {}) => {
      const lines = text.split("\n");
      let currentY = y;
      for (const line of lines) {
        const words = line.split(" ");
        let wrappedLines = [];
        let currentLine = "";
        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const metrics = ctx.measureText(testLine);
          if (metrics.width > maxWidth && currentLine) {
            wrappedLines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        wrappedLines.push(currentLine);

        for (let i = 0; i < wrappedLines.length; i++) {
          const isLastLine = i === wrappedLines.length - 1;
          if (justify && !isLastLine) {
            const lineWords = wrappedLines[i].split(" ");
            if (lineWords.length > 1) {
              const totalWordWidth = lineWords.reduce(
                (sum, w) => sum + ctx.measureText(w).width,
                0,
              );
              const totalSpace = maxWidth - totalWordWidth;
              const spaceWidth = totalSpace / (lineWords.length - 1);
              let curX = x;
              for (let j = 0; j < lineWords.length; j++) {
                ctx.fillText(lineWords[j], curX, currentY);
                curX += ctx.measureText(lineWords[j]).width + spaceWidth;
              }
            } else {
              ctx.fillText(wrappedLines[i], x, currentY);
            }
          } else {
            ctx.fillText(wrappedLines[i], x, currentY);
          }
          currentY += lineHeight;
        }
      }
      return currentY;
    },
    [],
  );

  async function handleGenerate() {
    if (!to.trim() && !from.trim() && !message.trim()) return;
    setIsGenerating(true);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const W = 1200;
      const H = 800;
      canvas.width = W;
      canvas.height = H;

      // Background — warm cream
      ctx.fillStyle = "#FDF6E3";
      ctx.fillRect(0, 0, W, H);

      // Subtle border
      ctx.strokeStyle = "#D4C5A9";
      ctx.lineWidth = 3;
      ctx.strokeRect(12, 12, W - 24, H - 24);

      // Inner decorative border
      ctx.strokeStyle = "#E8DCC8";
      ctx.lineWidth = 1;
      ctx.strokeRect(20, 20, W - 40, H - 40);

      // Divider line — vertical, splitting left (message) and right (address)
      const dividerX = W * 0.55;
      ctx.strokeStyle = "#C4B494";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(dividerX, 40);
      ctx.lineTo(dividerX, H - 40);
      ctx.stroke();

      // === LEFT SIDE: Message ===
      // Message text — large and justified
      ctx.fillStyle = "#3B2F1E";
      ctx.font = "28px Georgia, serif";
      wrapText(ctx, message || "", 50, 80, dividerX - 100, 38, {
        justify: true,
      });

      // === RIGHT SIDE: Stamp area + Address ===

      // Stamp box (top-right)
      const stampBoxSize = 240;
      const stampX = W - 50 - stampBoxSize;
      const stampY = 40;

      // Stamp border (perforated look — no fill, preserve transparency)
      ctx.strokeStyle = "#B8A88A";
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(
        stampX - 6,
        stampY - 6,
        stampBoxSize + 12,
        stampBoxSize + 12,
      );
      ctx.setLineDash([]);

      // Load and draw stamp image
      if (stampImageUrl) {
        try {
          // data: URLs (e.g. dev seed images) can be drawn directly without
          // tainting the canvas; S3 URLs go through the backend proxy to
          // avoid CORS issues
          let src = stampImageUrl;
          let blobUrl = null;
          if (!stampImageUrl.startsWith("data:")) {
            const baseUrl = process.env.REACT_APP_BASE_URL || "";
            const proxyUrl = `${baseUrl}/proxy-image?url=${encodeURIComponent(stampImageUrl)}`;
            const imgBlob = await fetch(proxyUrl).then((r) => {
              if (!r.ok) throw new Error("Proxy fetch failed");
              return r.blob();
            });
            blobUrl = URL.createObjectURL(imgBlob);
            src = blobUrl;
          }
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = () => {
              if (blobUrl) URL.revokeObjectURL(blobUrl);
              resolve();
            };
            img.onerror = () => {
              if (blobUrl) URL.revokeObjectURL(blobUrl);
              reject(new Error("Image load failed"));
            };
            img.src = src;
          });
          // Fit image into stamp box preserving aspect ratio
          const scale = Math.min(
            stampBoxSize / img.width,
            stampBoxSize / img.height,
          );
          const sw = img.width * scale;
          const sh = img.height * scale;
          const sx = stampX + (stampBoxSize - sw) / 2;
          const sy = stampY + (stampBoxSize - sh) / 2;
          ctx.drawImage(img, sx, sy, sw, sh);
        } catch {
          // Fallback if image can't load
          ctx.fillStyle = "#E8DCC8";
          ctx.fillRect(stampX, stampY, stampBoxSize, stampBoxSize);
          ctx.fillStyle = "#B8A88A";
          ctx.font = "14px Georgia, serif";
          ctx.textAlign = "center";
          ctx.fillText(
            "STAMP",
            stampX + stampBoxSize / 2,
            stampY + stampBoxSize / 2 + 5,
          );
          ctx.textAlign = "start";
        }
      }

      // Address lines with inline To: and From: labels
      const addressStartY = 360;
      const lineSpacing = 50;
      ctx.strokeStyle = "#C4B494";
      ctx.lineWidth = 1;

      // "To:" lines
      const toLines = (to || "").split("\n");
      for (let i = 0; i < Math.max(3, toLines.length); i++) {
        const ly = addressStartY + i * lineSpacing;
        ctx.beginPath();
        ctx.moveTo(dividerX + 30, ly);
        ctx.lineTo(W - 50, ly);
        ctx.stroke();

        if (i === 0) {
          // "To:" label inline
          ctx.fillStyle = "#5C4A32";
          ctx.font = "italic 20px Georgia, serif";
          ctx.fillText("To:", dividerX + 35, ly - 10);
        }
        if (toLines[i]) {
          ctx.fillStyle = "#3B2F1E";
          ctx.font = "22px Georgia, serif";
          const textX = i === 0 ? dividerX + 80 : dividerX + 35;
          ctx.fillText(toLines[i], textX, ly - 10);
        }
      }

      // "From:" lines
      const fromStartY =
        addressStartY + Math.max(3, toLines.length) * lineSpacing + 20;
      const fromLines = (from || "").split("\n");
      for (let i = 0; i < Math.max(2, fromLines.length); i++) {
        const ly = fromStartY + i * lineSpacing;
        ctx.beginPath();
        ctx.moveTo(dividerX + 30, ly);
        ctx.lineTo(W - 50, ly);
        ctx.stroke();

        if (i === 0) {
          ctx.fillStyle = "#5C4A32";
          ctx.font = "italic 20px Georgia, serif";
          ctx.fillText("From:", dividerX + 35, ly - 10);
        }
        if (fromLines[i]) {
          ctx.fillStyle = "#3B2F1E";
          ctx.font = "22px Georgia, serif";
          const textX = i === 0 ? dividerX + 100 : dividerX + 35;
          ctx.fillText(fromLines[i], textX, ly - 10);
        }
      }

      const url = canvas.toDataURL("image/png");
      setPreviewUrl(url);
    } catch (err) {
      console.error("Error generating postcard:", err);
    } finally {
      setIsGenerating(false);
    }
  }

  function handleDownload() {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `postcard-${stampSubject || "stamp"}.png`;
    a.click();
  }

  async function handleShare() {
    if (!previewUrl) return;
    try {
      const res = await fetch(previewUrl);
      const blob = await res.blob();
      const file = new File([blob], "postcard.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Postcard" });
      } else {
        handleDownload();
      }
    } catch {
      handleDownload();
    }
  }

  return (
    <div className="border-t border-ink/10 pt-8">
      <div className="rounded-xl bg-cream border border-ink/10 p-6 md:p-8">
        <h3 className="text-base font-medium text-ink mb-5 tracking-wide">
          <HiOutlineEnvelope className="inline w-4 h-4 mr-1 -mt-0.5" /> Compose
          your postcard
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div>
            <label className="block font-mono text-xs font-medium text-ink/50 uppercase tracking-wider mb-1.5">
              To
            </label>
            <input
              type="text"
              className="w-full bg-white border border-ink/20 rounded-lg px-3 py-2.5 text-sm text-ink placeholder-ink/40 focus:outline-none focus:border-ink/60 focus:ring-1 focus:ring-ink/10 transition-all duration-200"
              placeholder="Recipient name & address"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div>
            <label className="block font-mono text-xs font-medium text-ink/50 uppercase tracking-wider mb-1.5">
              From
            </label>
            <input
              type="text"
              className="w-full bg-white border border-ink/20 rounded-lg px-3 py-2.5 text-sm text-ink placeholder-ink/40 focus:outline-none focus:border-ink/60 focus:ring-1 focus:ring-ink/10 transition-all duration-200"
              placeholder="Your name & address"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block font-mono text-xs font-medium text-ink/50 uppercase tracking-wider mb-1.5">
            Message
          </label>
          <textarea
            className="w-full bg-white border border-ink/20 rounded-lg px-3 py-2.5 text-sm text-ink placeholder-ink/40 focus:outline-none focus:border-ink/60 focus:ring-1 focus:ring-ink/10 resize-none transition-all duration-200"
            rows={3}
            placeholder="Write your postcard message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>

        <button
          onClick={handleGenerate}
          disabled={
            isGenerating || (!to.trim() && !from.trim() && !message.trim())
          }
          className="px-7 py-2.5 text-sm font-medium rounded-lg bg-ink text-paper hover:bg-ink/85 hover:shadow-md active:scale-[0.97] transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:active:scale-100"
        >
          {isGenerating ? "Generating…" : "Generate preview"}
        </button>
      </div>

      {/* Hidden canvas */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Preview */}
      {previewUrl && (
        <div className="mt-8 animate-[fadeSlideIn_0.3s_ease-out]">
          <p className="font-mono text-xs font-medium text-ink/50 uppercase tracking-wider mb-3">
            Your postcard
          </p>
          <img
            src={previewUrl}
            alt="Generated postcard"
            className="w-full max-w-[800px] rounded-xl shadow-lg shadow-ink/15 border border-ink/10"
          />
          <div className="flex gap-3 mt-5">
            <button
              onClick={handleDownload}
              className="px-5 py-2 text-sm font-medium rounded-lg border border-ink/25 text-ink/70 hover:border-ink/60 hover:text-ink hover:bg-ink/5 active:scale-[0.97] transition-all duration-200"
            >
              Download
            </button>
            <button
              onClick={handleShare}
              className="px-5 py-2 text-sm font-medium rounded-lg border border-ink/25 text-ink/70 hover:border-ink/60 hover:text-ink hover:bg-ink/5 active:scale-[0.97] transition-all duration-200"
            >
              Share
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
